import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  screenerAnalyzePlanV2,
  screenerUploadCsvV2,

} from '../services/api';
import { apiFetch, apiStream, apiUrl } from '../services/apiClient';
import {
  getTierNameFromUser,
  hasMinimumTierForUser,
  tierLimits,
  useAuthStore,
} from '../stores/authStore';
import { useWorkspaceStore } from '../stores/workspaceStore';
import { setB2BMode } from '../hooks/useB2BMode';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // === 사용자 인증 State (Zustand) ===
  const user = useAuthStore((state) => state.user);
  const authToken = useAuthStore((state) => state.authToken);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const sessionChecked = useAuthStore((state) => state.sessionChecked);
  const setUser = useAuthStore((state) => state.setUser);
  const setAuthToken = useAuthStore((state) => state.setAuthToken);
  const setIsLoggedIn = useAuthStore((state) => state.setIsLoggedIn);
  const authLoginUser = useAuthStore((state) => state.loginUser);
  const authLogoutUser = useAuthStore((state) => state.logoutUser);
  const checkSession = useAuthStore((state) => state.checkSession);

  // === 프로젝트/조사 State (Zustand) ===
  const projects = useWorkspaceStore((state) => state.projects);
  const studies = useWorkspaceStore((state) => state.studies);
  const recentArtifacts = useWorkspaceStore((state) => state.recentArtifacts);
  const currentProject = useWorkspaceStore((state) => state.currentProject);
  const currentStudy = useWorkspaceStore((state) => state.currentStudy);
  const projectsLoading = useWorkspaceStore((state) => state.projectsLoading);
  const studiesLoading = useWorkspaceStore((state) => state.studiesLoading);
  const workspaceLoaded = useWorkspaceStore((state) => state.workspaceLoaded);
  const setProjects = useWorkspaceStore((state) => state.setProjects);
  const setStudies = useWorkspaceStore((state) => state.setStudies);
  const setCurrentProject = useWorkspaceStore((state) => state.setCurrentProject);
  const setCurrentStudy = useWorkspaceStore((state) => state.setCurrentStudy);
  const resetWorkspace = useWorkspaceStore((state) => state.resetWorkspace);
  const loadProjects = useWorkspaceStore((state) => state.loadProjects);
  const createProject = useWorkspaceStore((state) => state.createProject);
  const getProject = useWorkspaceStore((state) => state.getProject);
  const updateProject = useWorkspaceStore((state) => state.updateProject);
  const deleteProject = useWorkspaceStore((state) => state.deleteProject);
  const loadStudiesByProject = useWorkspaceStore((state) => state.loadStudiesByProject);
  const createStudy = useWorkspaceStore((state) => state.createStudy);
  const getStudy = useWorkspaceStore((state) => state.getStudy);
  const getStudiesByProject = useWorkspaceStore((state) => state.getStudiesByProject);
  const updateStudy = useWorkspaceStore((state) => state.updateStudy);
  const deleteStudy = useWorkspaceStore((state) => state.deleteStudy);

  // 기존 State들
  const [finalPlanText, setFinalPlanText] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);

  // --- [신규] App 6: Guideline Generator State ---
  const [guidelineExtractLoading, setGuidelineExtractLoading] = useState(false); // [FIX-1] 방법론 추출 로딩
  const [guidelineGenerateLoading, setGuidelineGenerateLoading] = useState(false); // [FIX-2] 최종 생성 로딩
  const [generatedGuideline, setGeneratedGuideline] = useState(null);
  const [extractedMethods, setExtractedMethods] = useState([]);

  const [screenerState, setScreenerState] = useState({
    planContent: '', planJson: null, csvContent: null, csvInfo: null,
    mappingResult: null, criteriaResult: null, scoredData: null,
    finalSelection: null, participantsData: [], // ⭐ 추가
    message: '',
    sincerityRules: null, // <-- [FIX] 성실도 규칙 JSON을 저장할 곳
    schemaAnalysisText: '', // <-- [FIX] AI가 생성한 요약 텍스트를 저장할 곳
    scheduleColumns: [], // 일정 컬럼 배열
  });
  const [screenerLoading, setScreenerLoading] = useState(null);
  
  // 단계별 로딩 상태 분리
  const [step1Loading, setStep1Loading] = useState(false);
  const [step2Loading, setStep2Loading] = useState(false);
  const [step3Loading, setStep3Loading] = useState(false);
  
  // SSE reader를 저장할 ref (중단 시 사용)
  const step3ReaderRef = useRef(null);
  
  // 실행 번호를 추적하여 취소된 실행의 결과가 상태에 저장되지 않도록 함
  const step3ExecutionIdRef = useRef(0);

  const resetFlowState = () => {
    // 이전 사용자의 모든 상태 초기화 (계정 전환 시 깨끗한 상태로 시작)
    resetWorkspace();
    
    // 스터디 관련 상태 초기화
    setFinalPlanText('');
    
    // Screener 상태 초기화
    setScreenerState({
      planContent: '', 
      planJson: null, 
      csvContent: null, 
      csvInfo: null,
      mappingResult: null, 
      criteriaResult: null, 
      scoredData: null,
      finalSelection: null, 
      participantsData: [],
      message: '',
      sincerityRules: null,
      schemaAnalysisText: '',
      scheduleColumns: [],
    });
    
    // Guideline Generator 상태 초기화
    setGeneratedGuideline(null);
    setExtractedMethods([]);
    
    // 로딩 상태 초기화
    setLoading(false);
    setGenerating(null);
    setScreenerLoading(null);
    setStep1Loading(false);
    setStep2Loading(false);
    setStep3Loading(false);
    setGuidelineExtractLoading(false);
    setGuidelineGenerateLoading(false);
  };

  // === 사용자 인증 Handlers ===
  const loginUser = (userData, accessToken) => {
    resetFlowState();
    authLoginUser(userData, accessToken);
  };

  const logoutUser = async () => {
    await authLogoutUser();
    resetWorkspace();
  };

  // Tier 체크 헬퍼 함수들
  const hasMinimumTier = (requiredTier) => {
    return hasMinimumTierForUser(user, requiredTier);
  };

  const isTier = (tier) => {
    return user && user.tier === tier;
  };

  const getTierName = () => {
    return getTierNameFromUser(user);
  };

  const canCreateProject = () => {
    if (!user) return false;
    const limit = tierLimits[user.tier || 'free'];
    return useWorkspaceStore.getState().projects.length < limit.projects;
  };

  const canCreateStudy = (projectId) => {
    if (!user) return false;
    const limit = tierLimits[user.tier || 'free'];

    // Free-tier global study cap disabled — token quota is the gate instead.
    // if (user.tier === 'free') {
    //   return studies.length < limit.studies;
    // }

    const projectStudies = useWorkspaceStore.getState().studies.filter(
      (study) => String(study.project_id) === String(projectId)
    );
    return projectStudies.length < limit.studies;
  };

  const getProjectLimit = () => {
    if (!user) return tierLimits.free.projects;
    return tierLimits[user.tier || 'free'].projects;
  };

  const getStudyLimit = () => {
    if (!user) return tierLimits.free.studies;
    return tierLimits[user.tier || 'free'].studies;
  };

  // 앱 시작 시 세션 상태 확인 (HttpOnly Cookie 기반)
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // 세션 확인 완료 후, 비로그인 상태이면 로그인 페이지로 리다이렉트
  // (checkSession이 완료된 후에만 리다이렉트하여 레이스 컨디션 방지)
  useEffect(() => {
    if (!sessionChecked) return;
    if (!isLoggedIn) {
      const currentPath = window.location.pathname;
      const publicPaths = [
        '/',
        '/login',
        '/individual/login',
        '/super/login',
        '/auth/callback',
        '/change-password',
        '/inquiry',
        '/terms',
        '/privacy',
        '/intro-legacy',
        '/tests/ui/shareview',
        '/tests/ab/shareview',
        '/tests/interview/shareview',
        // Retired routes: allow through so they can Navigate home (not fake session_expired)
        '/demo',
        '/secret-admin-dashboard-x7y9z3',
      ];
      const isPublicPath = publicPaths.some((p) => (
        p === '/'
          ? currentPath === '/'
          : currentPath === p || currentPath.startsWith(`${p}/`)
      ));
      if (!isPublicPath) {
        const loginPath = currentPath.startsWith('/super-admin') ? '/super/login' : '/individual/login';
        window.location.href = `${loginPath}?session_expired=true`;
      }
    }
  }, [sessionChecked, isLoggedIn]);

  // 로그인 사용자는 B2B 통합 셸 사용 (super admin 제외)
  useEffect(() => {
    if (!sessionChecked || !isLoggedIn || !user) return;
    if (user.tier === 'super') return;
    setB2BMode(true);
  }, [sessionChecked, isLoggedIn, user, user?.tier]);

  // 워크스페이스 데이터는 필요한 화면이 요청한다. 로그아웃 시에는 사용자 데이터를 비운다.
  useEffect(() => {
    if (!sessionChecked) return;
    const workspaceAccessAllowed = isLoggedIn && !user?.password_reset_required;
    if (!workspaceAccessAllowed && (
      workspaceLoaded || projectsLoading || projects.length || studies.length || recentArtifacts.length
    )) {
      resetWorkspace();
    }
  }, [
    isLoggedIn,
    user?.password_reset_required,
    sessionChecked,
    workspaceLoaded,
    projectsLoading,
    projects.length,
    recentArtifacts.length,
    resetWorkspace,
    studies.length,
  ]);

  // NOTE: Legacy generator/diagnoser/survey-builder flows were removed with pages/old cleanup.

// [FIX] 4단계: Screener 핸들러 6개 전체 로직 구현
// AppContext.js의 기존 6개 { /* ... */ } 스텁을 아래 코드로 전체 교체하십시오.
  // 1. 계획서 분석 핸들러
    const initialScreenerDownstreamState = {
    planJson: null, 
    csvContent: null, 
    csvInfo: null,
    mappingResult: null, 
    criteriaResult: null, 
    scoredData: null,
    finalSelection: null,
    participantsData: [], // ⭐ 추가
    message: ''
  };
  
  // [미사용] 현행은 B2B/StudyWorkflow가 create-and-generate 직접 호출. 하위 호환용 no-op
  const handleGenerateGuideline = async () => {};
  const handleExtractMethodologies = async () => {};

  // 1. 스터디의 최종 계획서 로드
  const loadStudyPlanFromDB = async (studyId) => {
    try {
      const response = await apiFetch(`/studies/${studyId}/artifacts`, {
        method: 'GET',
      });
      if (response.ok) {
        const data = await response.json();
        const planArtifact = data.artifacts?.find(a => a.artifact_type === 'plan');
        
        if (planArtifact) {
          return planArtifact.content;
        }
      }
      return null;
    } catch (error) {
      console.error('계획서 로드 실패:', error);
      return null;
    }
  };

  // 2. 스크리너 설문에서 Step1 데이터 로드
  const loadStep1FromSurvey = async (studyId) => {
    try {
      const response = await apiFetch(`/studies/${studyId}/artifacts`, {
        method: 'GET',
      });
      if (response.ok) {
        const data = await response.json();
        const surveyArtifact = data.artifacts?.find(a => a.artifact_type === 'survey');
        
        if (surveyArtifact) {
          // Survey artifact의 content에서 JSON 데이터 추출
          const match = surveyArtifact.content.match(/<!-- SURVEY_DATA\n([\s\S]*?)\n-->/);
          if (match) {
            const surveyData = JSON.parse(match[1]);
            if (surveyData.key_variables || surveyData.target_groups) {
              setScreenerState(prev => ({ ...prev, planJson: surveyData }));
              return true; // Step1 데이터 로드 성공
            }
          }
        }
      }
      return false; // Step1 데이터 없음
    } catch (error) {
      console.error('Step1 데이터 로드 실패:', error);
      return false;
    }
  };

  // 3. 스크리너 데이터 전체 로드
  const loadScreenerDataFromDB = async (studyId) => {
    try {
      const response = await apiFetch(`/studies/${studyId}/artifacts`, {
        method: 'GET',
      });
      if (response.ok) {
        const data = await response.json();
        const surveyArtifact = data.artifacts?.find(a => a.artifact_type === 'survey');
        
        if (surveyArtifact) {
          const match = surveyArtifact.content.match(/<!-- SURVEY_DATA\n([\s\S]*?)\n-->/);
          if (match) {
            const surveyData = JSON.parse(match[1]);
            
            // ✅ 상태를 직접 설정
            setScreenerState(prev => ({ ...prev, planJson: surveyData }));
            
            return {
              step1: { data: surveyData }
            };
          }
        }
      }
      return null;
    } catch (error) {
      console.error('스크리너 데이터 로드 실패:', error);
      return null;
    }
  };

  // frontend/src/context/AppContext.js에 추가할 함수들

  const handleScreenerPlanAnalysisV2 = async (planText, studyId) => {
    if (!planText.trim()) return alert("분석할 계획서를 입력해주세요.");
    
    setStep1Loading(true); // 1단계 로딩 시작
    setScreenerState(prev => ({ ...prev, ...initialScreenerDownstreamState }));

    try {
      const res = await screenerAnalyzePlanV2(planText, studyId);
      if (!res.success) throw new Error(res.error);
      setScreenerState(prev => ({ ...prev, planJson: res.analysis }));
    } catch (err) {
      alert(`[Screener 1] 계획서 분석 실패: ${err.message}`);
    } finally {
      setStep1Loading(false); // 1단계 로딩 종료
    }
  };

  // planJson 수동 업데이트 함수 (편집 기능용)
  const updatePlanJson = (updatedPlanJson) => {
    setScreenerState(prev => ({ ...prev, planJson: updatedPlanJson }));
  };

  // frontend/src/context/AppContext.js에 추가

  const handleScreenerUploadV2 = async (csvContent) => {
    // ⚠️ 새 CSV 업로드 시 이전 실행 무효화
    step3ExecutionIdRef.current++;
    console.log(`📋 CSV 업로드 - 실행 ID 증가: ${step3ExecutionIdRef.current}`);
    
    setStep2Loading(true); // 2단계 로딩 시작
    setScreenerState(prev => ({ 
      ...prev, 
      csvContent: csvContent,
      csvInfo: null,
      mappingResult: null, 
      criteriaResult: null, 
      scoredData: null,
      finalSelection: null, 
      sincerityRules: null,
      schemaAnalysisText: '',
      scheduleColumns: [],
      message: ''
    }));

    try {
      const res = await screenerUploadCsvV2(csvContent);
      if (!res.success) throw new Error(res.error);
      
      // scheduleColumns 추출
      const scheduleColumns = res.sincerity_rules?.schedule_columns || [];
      
      setScreenerState(prev => ({ 
        ...prev, 
        csvInfo: res.csv_info,
        sincerityRules: res.sincerity_rules,
        schemaAnalysisText: res.schema_analysis_text,
        scheduleColumns: scheduleColumns
      }));
    } catch (err) {
      alert(`[Screener 2] CSV 업로드 실패: ${err.message}`);
    } finally {
      setStep2Loading(false); // 2단계 로딩 종료
    }
  };

  const handleFindOptimalParticipants = async (studyId, csvContent, csvInfo, planJson, sincerityRules) => {
    console.log('🚀 handleFindOptimalParticipants 시작');
    
    // 새로운 실행 ID 생성 (이전 실행보다 무조건 큰 숫자)
    const currentExecutionId = ++step3ExecutionIdRef.current;
    console.log(`📋 실행 ID: ${currentExecutionId}`);
    
    setStep3Loading(true);
    
    try {
      console.log('📤 API 호출:', apiUrl('/screener/find-optimal-participants'));
      
      // POST 요청으로 데이터 전송
      const response = await apiStream('/screener/find-optimal-participants', {
        method: 'POST',
        body: JSON.stringify({
          study_id: studyId,
          csv_content: csvContent,
          csv_info: csvInfo,
          plan_json: planJson,
          sincerity_rules: sincerityRules
        })
      });
  
      console.log('📥 Response status:', response.status, response.ok);
    
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    
      // 응답을 스트리밍으로 읽기
      const reader = response.body.getReader();
      step3ReaderRef.current = reader; // reader를 ref에 저장 (중단 시 사용)
      const decoder = new TextDecoder();
      
      // 🌟 버퍼: 불완전한 라인 저장
      let buffer = '';
  
      console.log('🌀 스트림 읽기 시작');
    
      while (true) {
        // 실행 중 취소되었는지 확인
        if (step3ExecutionIdRef.current !== currentExecutionId) {
          console.log('⚠️ 이전 실행이 취소되었습니다. 데이터 처리 중단.');
          reader.cancel();
          return;
        }
        
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ 스트림 완료 (done=true)');
          
          // 실행이 취소되었는지 확인
          if (step3ExecutionIdRef.current !== currentExecutionId) {
            console.log('⚠️ 실행이 취소되었습니다. 버퍼 처리 중단.');
            return;
          }
          
          // 남은 버퍼 처리
          if (buffer.trim()) {
            console.log('⚠️ 남은 버퍼 처리');
            const lines = buffer.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                // 실행 중 취소되었는지 확인
                if (step3ExecutionIdRef.current !== currentExecutionId) {
                  console.log('⚠️ 버퍼 처리 중 실행이 취소되었습니다.');
                  return;
                }
                
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  // 에러 처리 추가
                  if (data.error) {
                    console.error('❌ 에러 응답 (버퍼):', data.error);
                    alert(`오류 발생: ${data.error}`);
                    setStep3Loading(false);
                    break;
                  }
                  
                  if (data.step === 4) {
                    setScreenerState(prev => ({...prev, finalSelection: data.final_selection}));
                    setStep3Loading(false);
                  } else if (data.step === 3) {
                    setScreenerState(prev => ({...prev, scoredData: data.scored_data, csvInfo: data.csv_info}));
                    setStep3Loading(false);
                  }
                } catch (e) {
                  console.error('❌ 버퍼 파싱 오류:', e);
                  setStep3Loading(false);
                }
              }
            }
          }
          
          setStep3Loading(false);
          break;
        }
  
        // 🌟 stream: true로 불완전한 유니코드 보존
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        console.log('📦 받은 chunk:', chunk.substring(0, 100));
        
        // 완전한 라인들만 처리
        const lines = buffer.split('\n');
        
        // 🌟 마지막 라인은 아직 완성되지 않았을 수 있으므로 버퍼에 저장
        buffer = lines.pop() || '';
        
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            // 실행 중 취소되었는지 확인
            if (step3ExecutionIdRef.current !== currentExecutionId) {
              console.log('⚠️ 실행이 취소되었습니다. 데이터 처리 중단.');
              reader.cancel();
              return;
            }
            
            try {
              const jsonStr = line.slice(6);
              console.log('📝 파싱하려는 JSON:', jsonStr.substring(0, 100));
              const data = JSON.parse(jsonStr);
              console.log('✨ 파싱 성공, step:', data.step);
              
              // 에러 처리 추가
              if (data.error) {
                console.error('❌ 에러 응답:', data.error);
                alert(`오류 발생: ${data.error}`);
                setStep3Loading(false);
                break;
              }
              
              // 현재 실행 ID 로그 추가 (디버깅용)
              console.log(`🔍 현재 실행 ID: ${currentExecutionId}, 최신 ID: ${step3ExecutionIdRef.current}`);
              
              if (data.step === 1) {
                console.log('🔵 Step 1 데이터 저장');
                setScreenerState(prev => ({...prev, mappingResult: data.mapping_result}));
              } else if (data.step === 2) {
                console.log('🟢 Step 2 데이터 저장');
                setScreenerState(prev => ({...prev, criteriaResult: data.criteria_result}));
              } else if (data.step === 3) {
                console.log('🟡 Step 3 완료 - 점수 산정 완료');
                setScreenerState(prev => ({...prev, scoredData: data.scored_data, csvInfo: data.csv_info}));
                // Step 3 완료 시 스피너 멈춤
                setStep3Loading(false);
              } else if (data.step === 4) {
                console.log('🟣 Step 4 완료!', data);
                console.log('🟣 final_selection:', data.final_selection);
                console.log('🟣 participants_data:', data.participants_data?.length || 0, '명');
                setScreenerState(prev => ({
                  ...prev, 
                  finalSelection: data.final_selection, 
                  csvInfo: data.csv_info,
                  participantsData: data.participants_data || []  // ⭐ participants_data 저장
                }));
                setStep3Loading(false);  // Step 4 완료 시에도 로딩 종료
              }
            } catch (error) {
              console.error('❌ SSE 데이터 파싱 오류:', error, '원본 라인:', line);
              setStep3Loading(false);  // 파싱 에러 시에도 로딩 해제
            }
          }
        }
      }
      
    } catch (error) {
      // 실행 중 취소된 경우 에러 무시
      if (step3ExecutionIdRef.current !== currentExecutionId) {
        console.log('⚠️ 실행이 취소된 후 에러 발생, 무시합니다.');
        return;
      }
      console.error('💥 최적의 참여자 찾기 실패:', error);
    } finally {
      // 현재 실행이 맞는지 확인 후 로딩 해제
      if (step3ExecutionIdRef.current === currentExecutionId) {
        setStep3Loading(false);
      }
      step3ReaderRef.current = null; // reader 정리
    }
  };

  // CSV 및 Step 결과 초기화 함수
  const resetCsvAndStepResults = () => {
    console.log('🔄 파일 변경 시작 - 초기화 및 이전 실행 무효화');
    
    // ⚠️ 먼저 실행 번호를 증가시켜서 이전 실행을 무효화
    // (이렇게 하면 이후 도착하는 이전 실행의 응답이 무시됨)
    step3ExecutionIdRef.current++;
    console.log(`📋 실행 ID 증가: ${step3ExecutionIdRef.current}`);
    
    // 실행 중인 SSE 중단
    if (step3ReaderRef.current) {
      console.log('🛑 SSE reader 취소');
      step3ReaderRef.current.cancel();
      step3ReaderRef.current = null;
    }
    
    // Step 3 로딩 중지
    setStep3Loading(false);
    
    // 하위 결과들 초기화 (즉시 UI에서 제거)
    console.log('🗑️ 상태 초기화 중');
    setScreenerState(prev => ({
      ...prev,
      csvInfo: null,
      mappingResult: null,
      criteriaResult: null,
      scoredData: null,
      finalSelection: null,
      sincerityRules: null,
      schemaAnalysisText: '',
      scheduleColumns: [],
    }));
  };

  const value = {
    // === 사용자 인증 ===
    user, setUser, authToken, setAuthToken, isLoggedIn, setIsLoggedIn, sessionChecked,
    loginUser, logoutUser,
    
    // === Tier 체크 함수 ===
    hasMinimumTier, isTier, getTierName,
    canCreateProject, canCreateStudy, getProjectLimit, getStudyLimit,

    // === B2B/Enterprise 헬퍼 ===
    isEnterprise: user?.account_type === 'business' && user?.tier === 'enterprise',
    isSuperAdmin: user?.tier === 'super' || user?.tier === 'admin',
    needsPasswordReset: user?.password_reset_required === true,
    companyId: user?.company_id || null,
    companyName: user?.company_name || null,

    // === 프로젝트 관리 ===
    projects, setProjects, currentProject, setCurrentProject,
    projectsLoading,
    workspaceLoaded,
    createProject, getProject, updateProject, deleteProject,
    loadProjects,

    // === 조사(Study) 관리 ===
    studies, setStudies, currentStudy, setCurrentStudy, studiesLoading,
    createStudy, getStudy, getStudiesByProject, loadStudiesByProject, updateStudy, deleteStudy,
    
    // === 최근 생성한 아티팩트 ===
    recentArtifacts,

    // === Common global state ===
    finalPlanText, setFinalPlanText,
    loading, setLoading,
    generating, setGenerating,

    screenerState, screenerLoading,
    step1Loading, step2Loading, step3Loading,


    // [FIX] App 6 
    guidelineExtractLoading, // [FIX-1]
    guidelineGenerateLoading, // [FIX-2]
    generatedGuideline,
    extractedMethods,
    handleGenerateGuideline,
    handleExtractMethodologies,

    // 선별기 내의 step1 자동생성을 위한 함수들
    loadStudyPlanFromDB,
    loadStep1FromSurvey, 
    loadScreenerDataFromDB,

    handleScreenerPlanAnalysisV2,
    handleScreenerUploadV2,
    handleFindOptimalParticipants,
    resetCsvAndStepResults,
    updatePlanJson,
  };



  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);
